import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { API } from '../config';
import { APP_VERSION_LABEL } from '../appVersion';
import {
  countActiveOrdersNeedingAttention,
  countActiveServiceQueueOrders,
  countClosedOrdersWithUnreadWorkerNotes,
} from '../utils/orderAttention';
import { isAdmin } from '../utils/roles';
import {
  Package,
  ClipboardText,
  Archive,
  Users,
  CalendarBlank,
  Wrench,
  SignOut,
  Gear,
  ShieldCheck,
  ListMagnifyingGlass,
} from '@phosphor-icons/react';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const userIsAdmin = isAdmin(user);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [ordersAttentionCount, setOrdersAttentionCount] = useState(0);
  const [serviceAttentionCount, setServiceAttentionCount] = useState(0);
  const [closedOrdersAttentionCount, setClosedOrdersAttentionCount] = useState(0);
  const urlFrom = new URLSearchParams(location.search).get('from');
  const viewingClosedOrderDetail =
    location.pathname === '/orders' && urlFrom === 'closed';
  const viewingServiceOrderDetail =
    location.pathname === '/orders' &&
    (urlFrom === 'service' || urlFrom === 'service-archive');
  const viewingPlanningOrderDetail =
    location.pathname === '/orders' && urlFrom === 'planning';

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/orders`, { withCredentials: true });
        if (cancelled) return;
        setOrdersAttentionCount(countActiveOrdersNeedingAttention(res.data));
        setServiceAttentionCount(countActiveServiceQueueOrders(res.data));
        setClosedOrdersAttentionCount(countClosedOrdersWithUnreadWorkerNotes(res.data));
      } catch {
        if (!cancelled) {
          setOrdersAttentionCount(0);
          setServiceAttentionCount(0);
          setClosedOrdersAttentionCount(0);
        }
      }
    };
    void tick();
    const id = setInterval(tick, 28000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/offers', icon: Package, labelKey: 'offers' },
    { to: '/orders', icon: ClipboardText, labelKey: 'orders' },
    { to: '/closed-orders', icon: Archive, labelKey: 'closedOrders' },
    { to: '/teams', icon: Users, labelKey: 'teams' },
    { to: '/planning', icon: CalendarBlank, labelKey: 'planning' },
    { to: '/service', icon: Wrench, labelKey: 'service' },
  ];

  return (
    <div className="app-container flex">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00CBA9] to-[#00A88C] flex items-center justify-center">
              <span className="text-white font-bold text-lg">V</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">VMK</h1>
              <p className="text-[#64748b] text-xs">{t('planningSystemLabel')}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="mb-2 px-4">
            <span className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider">
              {t('mainMenu')}
            </span>
          </div>
          {navItems.map(({ to, icon: Icon, labelKey }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => {
                let active = isActive;
                if (
                  to === '/orders' &&
                  (viewingClosedOrderDetail ||
                    viewingServiceOrderDetail ||
                    viewingPlanningOrderDetail)
                ) {
                  active = false;
                }
                if (to === '/closed-orders' && viewingClosedOrderDetail) active = true;
                if (to === '/service' && viewingServiceOrderDetail) active = true;
                if (to === '/planning' && viewingPlanningOrderDetail) active = true;
                return `sidebar-nav-item ${active ? 'active' : ''}`;
              }}
              data-testid={`nav-${labelKey}`}
              title={
                to === '/orders' && ordersAttentionCount > 0
                  ? t('navOrdersAttentionTitle')
                  : to === '/closed-orders' && closedOrdersAttentionCount > 0
                    ? t('navClosedOrdersAttentionTitle')
                    : to === '/service' && serviceAttentionCount > 0
                      ? t('navServiceAttentionTitle')
                      : undefined
              }
            >
              <Icon size={20} weight="regular" />
              <span className="sidebar-nav-label-with-badge">
                <span>{t(labelKey)}</span>
                {to === '/orders' && ordersAttentionCount > 0 ? (
                  <span className="nav-orders-attention-dot" aria-hidden />
                ) : null}
                {to === '/closed-orders' && closedOrdersAttentionCount > 0 ? (
                  <span className="nav-orders-attention-dot" aria-hidden />
                ) : null}
                {to === '/service' && serviceAttentionCount > 0 ? (
                  <span className="nav-orders-attention-dot" aria-hidden />
                ) : null}
              </span>
            </NavLink>
          ))}
          {userIsAdmin ? (
            <div className="mb-2 mt-4 px-4">
              <span className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider">
                {t('adminSection')}
              </span>
            </div>
          ) : null}
          {userIsAdmin
            ? [
                { to: '/admin/users', icon: ShieldCheck, labelKey: 'users' },
                { to: '/admin/audit', icon: ListMagnifyingGlass, labelKey: 'auditTrail' },
              ].map(({ to, icon: Icon, labelKey }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${labelKey}`}
                >
                  <Icon size={20} weight="regular" />
                  <span>{t(labelKey)}</span>
                </NavLink>
              ))
            : null}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Settings Link */}
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `sidebar-nav-item mb-3 ${isActive ? 'active' : ''}`
            }
            data-testid="nav-settings"
          >
            <Gear size={20} weight="regular" />
            <span>{t('settings')}</span>
          </NavLink>

          {/* User Info */}
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#1e293b] mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00CBA9] to-[#00A88C] flex items-center justify-center text-white font-semibold text-sm">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-[#64748b] text-xs truncate">
                {user?.email}
                {userIsAdmin ? '' : ` · ${t('roleUser')}`}
              </p>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="sidebar-nav-item w-full text-[#ef4444] hover:bg-red-500/10"
            data-testid="logout-btn"
          >
            <SignOut size={20} />
            <span>{t('logout')}</span>
          </button>

          <p
            className="px-4 text-center text-[10px] text-[#64748b] tabular-nums mt-2"
            data-testid="sidebar-app-version"
          >
            {APP_VERSION_LABEL}
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
